import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Datepicker from "components/Input/Datepicker/Datepicker";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Selection from "components/Input/Selection/Selection";
import transactionCurrencyOptions from "utils/transactionCurrencyOptions/TransactionCurrencyOptions.jsx";
import s from "./CreateContent.module.scss";

const CreateContent = ({ handleInput, data }) => {
  const { t } = useTranslation();
  const buyRef = useRef(5);
  const sellRef = useRef(5);
  const [inputData, setInputData] = useState({
    exchangeDate: "",
    currencyCode: "",
    cost: "",
    fxRate: "",
    bankSellCost: "",
    bankSellFxRate: "",
  });
  const [focus, setFocus] = useState({});

  const textInputOnchange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    handleExchangeRateInput(value, field);
  };
  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };
  const textInputOnBlur = (field) => {
    setFocus({ [field]: false });
  };

  const exchangeDateOnChange = (e, type) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    const date = moment(rawDate).isValid()
      ? moment(rawDate).format("YYYY-MM-DD")
      : e !== null
      ? ""
      : null;
    if (type === "start") {
      setInputData({ ...inputData, exchangeDate: date });
      handleInput(date, "exchangeDate");
    }
  };

  const currencyCodeOnChange = (e) => {
    const { value } = e.target;
    setInputData({ ...inputData, currencyCode: value });
    handleInput(value, "currencyCode");
  };

  const fxRatePercentageOnChange = (e) => {
    const { value } = e.target;
    buyRef.current = +value;
  };

  const bankSellFxRatePercentageOnChange = (e) => {
    const { value } = e.target;
    sellRef.current = +value;
  };

  const handleExchangeRateInput = (value, field) => {
    const regex = /^\d*(\.\d{0,6})?$/g;
    const isValidInput = regex.test(value);
    if (!isValidInput) {
      return;
    }
    setInputData({ ...inputData, [field]: value });
    return handleInput(+Number(value).toFixed(6), field);
  };

  const calculateBuyFxRate = () => {
    const { cost } = data;
    const newFxRate = +cost * (1 - +buyRef.current / 100);
    setInputData({ ...inputData, fxRate: newFxRate });
    return handleInput(+Number(newFxRate).toFixed(6), "fxRate");
  };

  const calculateSellFxRate = () => {
    const { bankSellCost } = data;
    const newSellFxRate = +bankSellCost * (1 + +sellRef.current / 100);
    setInputData({ ...inputData, bankSellFxRate: newSellFxRate });
    return handleInput(+Number(newSellFxRate).toFixed(6), "bankSellFxRate");
  };

  return (
    <div className={s.contentContainer}>
      <div>
        <Datepicker
          label={t("page.finance.fxRate.create.function.dialog.content.1")}
          type="start"
          noErrorMessage
          onchangeFn={exchangeDateOnChange}
          onfocusFn={() => textInputOnFocus("exchangeDate")}
          onblurFn={() => textInputOnBlur("exchangeDate")}
          sxParams={{ datepickerFocus: focus.exchangeDate }}
        />
      </div>

      <div>
        <Selection
          label={t("page.finance.fxRate.create.function.dialog.content.2")}
          selectionValue={
            inputData.currencyCode ? inputData.currencyCode : "USD"
          }
          noErrorMessage
          onchangeFn={currencyCodeOnChange}
          onfocusFn={() => textInputOnFocus("currencyCode")}
          onblurFn={() => textInputOnBlur("currencyCode")}
          options={transactionCurrencyOptions("all")}
          sxParams={{ selectionFocus: focus.currencyCode }}
        />
      </div>

      <div>
        <OutlinedText
          label={t("page.finance.fxRate.create.function.dialog.content.3")}
          noErrorMessage
          value={inputData.cost ? inputData.cost : ""}
          onchangeFn={(e) => textInputOnchange(e, "cost")}
          onfocusFn={() => textInputOnFocus("cost")}
          onblurFn={() => textInputOnBlur("cost")}
          sxParams={{ textFocus: focus.cost }}
        />
      </div>

      <div>
        <OutlinedText
          label={t("page.finance.fxRate.create.function.dialog.content.4")}
          noErrorMessage
          value={inputData.fxRate ? inputData.fxRate : ""}
          onchangeFn={(e) => textInputOnchange(e, "fxRate")}
          onfocusFn={() => textInputOnFocus("fxRate")}
          onblurFn={() => textInputOnBlur("fxRate")}
          sxParams={{ textFocus: focus.fxRate }}
        />
      </div>

      <div>
        <OutlinedText
          label={t("page.finance.fxRate.create.function.dialog.content.5")}
          noErrorMessage
          defaultValue="5"
          onchangeFn={fxRatePercentageOnChange}
          onfocusFn={() => textInputOnFocus("fxRatePercentage")}
          onblurFn={() => textInputOnBlur("fxRatePercentage")}
          sxParams={{ textFocus: focus.fxRatePercentage }}
        />
      </div>

      <div className={s.calculateBtnContainer}>
        <button className={s.calculateBtn} onClick={calculateBuyFxRate}>
          {t("page.finance.fxRate.create.function.calculate.button.1")}
        </button>
      </div>

      <div>
        <OutlinedText
          label={t("page.finance.fxRate.create.function.dialog.content.6")}
          noErrorMessage
          value={inputData.bankSellCost ? inputData.bankSellCost : ""}
          onchangeFn={(e) => textInputOnchange(e, "bankSellCost")}
          onfocusFn={() => textInputOnFocus("bankSellCost")}
          onblurFn={() => textInputOnBlur("bankSellCost")}
          sxParams={{ textFocus: focus.bankSellCost }}
        />
      </div>

      <div>
        <OutlinedText
          label={t("page.finance.fxRate.create.function.dialog.content.7")}
          noErrorMessage
          value={inputData.bankSellFxRate ? inputData.bankSellFxRate : ""}
          onchangeFn={(e) => textInputOnchange(e, "bankSellFxRate")}
          onfocusFn={() => textInputOnFocus("bankSellFxRate")}
          onblurFn={() => textInputOnBlur("bankSellFxRate")}
          sxParams={{ textFocus: focus.bankSellFxRate }}
        />
      </div>

      <div>
        <OutlinedText
          label={t("page.finance.fxRate.create.function.dialog.content.8")}
          noErrorMessage
          defaultValue="5"
          onchangeFn={bankSellFxRatePercentageOnChange}
          onfocusFn={() => textInputOnFocus("bankSellFxRatePercentage")}
          onblurFn={() => textInputOnBlur("bankSellFxRatePercentage")}
          sxParams={{ textFocus: focus.bankSellFxRatePercentage }}
        />
      </div>

      <div className={s.calculateBtnContainer}>
        <button className={s.calculateBtn} onClick={calculateSellFxRate}>
          {t("page.finance.fxRate.create.function.calculate.button.2")}
        </button>
      </div>
    </div>
  );
};

export default React.memo(CreateContent);
